import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { mapSettled } from '@/lib/concurrency'
import { isYoutubeQuotaError } from '@/lib/errors'
import type { components } from '@/../types/api'

export type VideoItem = components['schemas']['VideoItemOut']
export type VideoList = components['schemas']['VideoListOut']

/** Sort keys accepted by the backend. Typed from the generated OpenAPI so a
 *  rename on the server side trips the frontend compiler immediately. */
export type VideosSort = 'recent' | 'ranking' | 'views'
/** Filtro de publicação: "published" = no ar ou agendado. */
export type VideosStatus = 'all' | 'published' | 'unpublished'
/**
 * "channel": o canal ativo (e o pool dele); "all": todos os canais e pools;
 * "pool": todo vídeo do pool do canal ativo, livre ou já levado por um canal.
 */
export type VideosScope = 'channel' | 'all' | 'pool'
/**
 * O balde de visibilidade de um publicado, para o subfiltro de "Publicados":
 * público (ou ainda não sincronizado), não listado, privado ou agendado.
 */
export type VideosVisibility = 'public' | 'unlisted' | 'private' | 'scheduled'

export interface UseVideosParams {
  channel: string
  page?: number
  limit?: number
  sort?: VideosSort
  status?: VideosStatus
  scope?: VideosScope
  /** Na visão do pool: os canais (ou o próprio pool) cujos vídeos ficam de fora. */
  exclude?: readonly string[]
  /** Com ``status="published"``: os baldes de visibilidade que ficam de fora. */
  hideVisibility?: readonly VideosVisibility[]
}

const NO_EXCLUDE: readonly string[] = []
const NO_VISIBILITY: readonly VideosVisibility[] = []

export function useVideos({
  channel,
  page = 1,
  limit = 24,
  sort = 'recent',
  status = 'all',
  scope = 'channel',
  exclude = NO_EXCLUDE,
  hideVisibility = NO_VISIBILITY,
}: UseVideosParams) {
  // Só a visão do pool filtra por canal; nas outras o parâmetro não vai.
  const hidden = scope === 'pool' ? exclude : NO_EXCLUDE
  // E só "Publicados" filtra por visibilidade.
  const hiddenVisibility = status === 'published' ? hideVisibility : NO_VISIBILITY
  return useQuery({
    queryKey: queryKeys.videos.list({
      channel,
      page,
      limit,
      sort,
      status,
      scope,
      exclude: hidden,
      hideVisibility: hiddenVisibility,
    }),
    queryFn: async (): Promise<VideoList> => {
      const { data } = await api.GET('/api/v1/videos', {
        params: {
          query: {
            channel,
            page,
            limit,
            sort,
            status,
            scope,
            exclude: [...hidden],
            hide_visibility: [...hiddenVisibility],
          },
        },
      })
      if (!data) throw new Error('videos response empty')
      return data.data
    },
    // "Todos os canais" não depende do canal ativo.
    enabled: scope === 'all' || Boolean(channel),
    // Keep last successful page visible while next page loads (no flash of empty)
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  })
}

export interface UseVideoParams {
  videoId: string
  lang: string
}

/**
 * Single-resource lookup for the Project Detail page. Hits the typed
 * `GET /api/v1/videos/{video_id}?lang=` endpoint. Disabled until both
 * `videoId` and `lang` are present so navigation placeholders don't
 * fire a 422.
 */
export function useVideo({ videoId, lang }: UseVideoParams) {
  return useQuery({
    queryKey: queryKeys.videos.byId(videoId, lang),
    queryFn: async (): Promise<VideoItem> => {
      const { data } = await api.GET('/api/v1/videos/{video_id}', {
        params: { path: { video_id: videoId }, query: { lang } },
      })
      if (!data) throw new Error('video not found')
      return data.data
    },
    enabled: Boolean(videoId) && Boolean(lang),
    staleTime: 5_000,
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface UpdateMetadataInput {
  videoId: string
  lang: string
  meta_title?: string
  meta_description?: string
}

/**
 * PATCH /videos/{id}/metadata with optimistic cache update. The byId cache
 * entry is flipped to the new title immediately so the Project Detail page
 * reflects the rename with zero flicker. On error we roll back to the prior
 * snapshot; on settle we invalidate to re-sync with the server source.
 */
export function useUpdateVideoMetadata() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateMetadataInput) => {
      const body: Record<string, string> = {}
      if (input.meta_title !== undefined) body['meta_title'] = input.meta_title
      if (input.meta_description !== undefined) body['meta_description'] = input.meta_description
      const { data } = await api.PATCH('/api/v1/videos/{video_id}/metadata', {
        params: { path: { video_id: input.videoId }, query: { lang: input.lang } },
        body,
      })
      return data?.data ?? { updated: [] }
    },
    onMutate: async (input) => {
      const key = queryKeys.videos.byId(input.videoId, input.lang)
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<VideoItem>(key)
      if (previous) {
        const patch: Partial<VideoItem> = {}
        if (input.meta_title !== undefined) patch.meta_title = input.meta_title
        if (input.meta_description !== undefined) patch.meta_description = input.meta_description
        qc.setQueryData<VideoItem>(key, { ...previous, ...patch })
      }
      return { previous, key }
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous)
    },
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: queryKeys.videos.byId(input.videoId, input.lang) })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

export interface DeleteVideoInput {
  videoId: string
  lang: string
  channel: string
}

/**
 * DELETE /videos/{id}. The backend resolves ``channel`` from the query
 * param so we pass it through. Invalidates the byId cache (by removing it)
 * and the entire videos.all tree to force the grid to refetch.
 */
export function useDeleteVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: DeleteVideoInput) => {
      const { data } = await api.DELETE('/api/v1/videos/{video_id}', {
        params: {
          path: { video_id: input.videoId },
          query: { lang: input.lang, channel: input.channel },
        },
      })
      return data?.data ?? { ok: true }
    },
    onSuccess: (_data, input) => {
      qc.removeQueries({ queryKey: queryKeys.videos.byId(input.videoId, input.lang) })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

export interface ArchiveVideosResult {
  archived: DeleteVideoInput[]
  failed: { input: DeleteVideoInput; error: unknown }[]
}

/** Deletes remove a folder on disk each — a few at a time is plenty. */
const ARCHIVE_CONCURRENCY = 3

/**
 * Bulk "Arquivar" from the grid: the same DELETE as ``useDeleteVideo``, one
 * request per video. Every video settles on its own — one failure doesn't
 * stop the rest — so the caller gets both lists and can keep the failures
 * selected for a retry.
 */
export function useArchiveVideos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (inputs: readonly DeleteVideoInput[]): Promise<ArchiveVideosResult> => {
      const settled = await mapSettled(inputs, ARCHIVE_CONCURRENCY, (input) =>
        api.DELETE('/api/v1/videos/{video_id}', {
          params: {
            path: { video_id: input.videoId },
            query: { lang: input.lang, channel: input.channel },
          },
        }),
      )
      const result: ArchiveVideosResult = { archived: [], failed: [] }
      settled.forEach((outcome, i) => {
        const input = inputs[i]
        if (!input) return
        if (outcome.status === 'fulfilled') result.archived.push(input)
        else result.failed.push({ input, error: outcome.reason })
      })
      return result
    },
    onSuccess: (result) => {
      for (const input of result.archived) {
        qc.removeQueries({ queryKey: queryKeys.videos.byId(input.videoId, input.lang) })
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

// ---------------------------------------------------------------------------
// Visibilidade no YouTube (docs/plan-youtube-visibility.md)
// ---------------------------------------------------------------------------

export type YoutubePrivacy = components['schemas']['VisibilityIn']['privacy']
export type VisibilityItem = components['schemas']['VisibilityItemOut']

/** Teto de uma ação em massa: 25 × 51 unidades de cota. */
export const VISIBILITY_BATCH_MAX = 25

export interface VisibilityTarget {
  videoId: string
  lang: string
  /** Deduplica o lote: a visibilidade é do vídeo do YouTube, não do projeto. */
  youtubeId: string
}

export type VisibilityOutcome =
  | { target: VisibilityTarget; state: 'ok' | 'unchanged'; result: VisibilityItem }
  | { target: VisibilityTarget; state: 'failed'; error: unknown }
  /** Não tentado: a cota do YouTube acabou num item anterior. */
  | { target: VisibilityTarget; state: 'skipped' }

async function patchVisibility(target: VisibilityTarget, privacy: YoutubePrivacy): Promise<VisibilityItem> {
  const { data } = await api.PATCH('/api/v1/videos/{video_id}/youtube/visibility', {
    params: { path: { video_id: target.videoId }, query: { lang: target.lang } },
    body: { privacy },
  })
  if (!data) throw new Error('Resposta vazia do servidor')
  return data.data
}

/**
 * O laço da ação em massa, puro para ser testável. **Sequencial**: cada item
 * é um ``videos.update`` de 50 unidades, e o backend é um processo só. Um item
 * por ``youtubeId``. Para no primeiro 429 de cota (``youtube-quota``): os
 * restantes saem ``skipped`` sem chamada, para não derrubar o upload do dia.
 * Qualquer outra falha (inclusive o 429 de "espere") segue para o próximo.
 * Recusa mais de ``VISIBILITY_BATCH_MAX`` itens sem chamar nada.
 */
export async function runVisibilityBatch(
  targets: readonly VisibilityTarget[],
  privacy: YoutubePrivacy,
  call: (target: VisibilityTarget, privacy: YoutubePrivacy) => Promise<VisibilityItem>,
  onProgress?: (outcome: VisibilityOutcome) => void,
): Promise<VisibilityOutcome[]> {
  const seen = new Set<string>()
  const unique = targets.filter((t) => {
    if (seen.has(t.youtubeId)) return false
    seen.add(t.youtubeId)
    return true
  })
  if (unique.length > VISIBILITY_BATCH_MAX) {
    throw new Error(`Até ${String(VISIBILITY_BATCH_MAX)} vídeos por vez`)
  }
  const outcomes: VisibilityOutcome[] = []
  let quotaHit = false
  for (const target of unique) {
    let outcome: VisibilityOutcome
    if (quotaHit) {
      outcome = { target, state: 'skipped' }
    } else {
      try {
        const result = await call(target, privacy)
        outcome = { target, state: result.status, result }
      } catch (error: unknown) {
        outcome = { target, state: 'failed', error }
        if (isYoutubeQuotaError(error)) quotaHit = true
      }
    }
    outcomes.push(outcome)
    onProgress?.(outcome)
  }
  return outcomes
}

function invalidateVisibility(qc: ReturnType<typeof useQueryClient>, targets: readonly VisibilityTarget[]): void {
  void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
  for (const t of targets) {
    void qc.invalidateQueries({ queryKey: queryKeys.analytics.videoYoutube(t.videoId, t.lang) })
  }
}

/** Um vídeo: ``PATCH /videos/{id}/youtube/visibility``. */
export function useSetVisibility() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { target: VisibilityTarget; privacy: YoutubePrivacy }) =>
      patchVisibility(input.target, input.privacy),
    onSettled: (_data, _err, input) => {
      invalidateVisibility(qc, [input.target])
    },
  })
}

export interface VisibilityBulkInput {
  targets: readonly VisibilityTarget[]
  privacy: YoutubePrivacy
  /** Chamado a cada item que termina, para a dialog mostrar o resultado. */
  onProgress?: (outcome: VisibilityOutcome) => void
}

/** A ação em massa da barra de seleção, pelo ``runVisibilityBatch``. */
export function useSetVisibilityBulk() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: VisibilityBulkInput) =>
      runVisibilityBatch(input.targets, input.privacy, patchVisibility, input.onProgress),
    onSettled: (_data, _err, input) => {
      invalidateVisibility(qc, input.targets)
    },
  })
}

export interface DuplicateVideoInput {
  videoId: string
  lang: string
  newId: string
}

/** POST /videos/{id}/duplicate. Returns the new id so the caller can
 * navigate to the duplicate's Project Detail page. */
export function useDuplicateVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: DuplicateVideoInput) => {
      const { data } = await api.POST('/api/v1/videos/{video_id}/duplicate', {
        params: { path: { video_id: input.videoId }, query: { lang: input.lang } },
        body: { new_id: input.newId },
      })
      if (!data) throw new Error('duplicate response empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}
